import React, { useEffect, useState } from 'react';
import MentionsLegales from './MentionsLegales';

// Page d'attente avant le lancement : le logo, un champ email, rien d'autre.
// Les adresses rejoignent la liste d'attente existante (POST /waitlist/join).
const API = process.env.REACT_APP_API_URL || 'https://api.nowatt.app';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const LEGAL_PATH = '/mentions-legales';

export default function ComingSoon() {
  const [legal, setLegal] = useState(() => window.location.pathname === LEGAL_PATH);
  const [email, setEmail] = useState('');

  // Le bouton « retour » du navigateur ramène de la page légale à la page d'attente
  useEffect(() => {
    const onPop = () => setLegal(window.location.pathname === LEGAL_PATH);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const openLegal = (e) => { e.preventDefault(); window.history.pushState(null, '', LEGAL_PATH); setLegal(true); window.scrollTo(0, 0); };
  const closeLegal = () => { window.history.pushState(null, '', '/'); setLegal(false); };
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) { setState('error'); setMessage('Vérifiez votre adresse email.'); return; }
    setState('sending');
    setMessage('');
    try {
      const res = await fetch(`${API}/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      if (!res.ok) throw new Error('REQUEST_FAILED');
      setState('done');
    } catch {
      setState('error');
      setMessage('Impossible d\'enregistrer votre adresse pour le moment. Réessayez dans un instant.');
    }
  };

  if (legal) return <MentionsLegales onBack={closeLegal} />;

  return (
    <div style={s.page}>
      <style>{`
        @keyframes cs-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes cs-glow { 0%, 100% { opacity: .35; } 50% { opacity: .6; } }
        .cs-input::placeholder { color: rgba(238,242,255,0.3); }
        .cs-input:focus { outline: none; border-color: rgba(200,241,53,0.55); }
        .cs-btn:hover:not(:disabled) { filter: brightness(1.08); }
        .cs-legal:hover { color: rgba(238,242,255,0.7); }
        .cs-btn:focus-visible, .cs-input:focus-visible, .cs-legal:focus-visible { outline: 2px solid #C8F135; outline-offset: 2px; }
      `}</style>
      <div style={s.glow} aria-hidden="true" />

      <main style={s.main}>
        <div style={s.brand}>
          <img src="/favicon_nowatt.png" alt="" style={s.mark} />
          <h1 style={s.word}><span style={{ color: '#C8F135' }}>No</span>watt</h1>
        </div>

        {state === 'done' ? (
          <p style={s.done} role="status">C'est noté ! On vous recontacte très vite avec tous les détails.</p>
        ) : (
          <>
            <p style={s.lead}>Laissez-nous votre email, on vous fait signe très vite.</p>
            <form onSubmit={submit} style={s.form} noValidate>
              <label htmlFor="cs-email" style={s.srOnly}>Adresse email</label>
              <input
                id="cs-email" className="cs-input" type="email" inputMode="email" autoComplete="email"
                placeholder="votre@email.fr" value={email}
                onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
                style={s.input}
              />
              <button className="cs-btn" type="submit" disabled={state === 'sending'} style={s.btn}>
                {state === 'sending' ? 'Envoi…' : 'Me prévenir'}
              </button>
            </form>
            <p style={s.error} role="alert">{state === 'error' ? message : ''}</p>
          </>
        )}
      </main>

      <footer style={s.footer}>
        <a className="cs-legal" href={LEGAL_PATH} onClick={openLegal} style={s.legalLink}>Mentions légales · Vos données</a>
      </footer>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: '#06080C', color: '#EEF2FF', position: 'relative', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px',
    fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box',
  },
  glow: {
    position: 'absolute', width: 520, height: 520, borderRadius: '50%', top: '50%', left: '50%',
    transform: 'translate(-50%, -62%)', background: 'radial-gradient(circle, rgba(200,241,53,0.16), transparent 65%)',
    animation: 'cs-glow 6s ease-in-out infinite', pointerEvents: 'none',
  },
  main: { position: 'relative', width: '100%', maxWidth: 420, textAlign: 'center', animation: 'cs-in .7s ease both' },
  brand: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 36 },
  mark: { width: 46, height: 54 },
  word: { margin: 0, fontFamily: "'Lexend', sans-serif", fontWeight: 800, fontSize: '2.6rem', letterSpacing: '-0.02em', lineHeight: 1 },
  lead: { margin: '0 0 20px', color: 'rgba(238,242,255,0.65)', fontSize: '0.98rem', lineHeight: 1.6 },
  form: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: {
    flex: '1 1 200px', minWidth: 0, background: 'rgba(238,242,255,0.04)', border: '1px solid rgba(238,242,255,0.12)',
    borderRadius: 10, padding: '14px 16px', color: '#EEF2FF', fontSize: '0.95rem', fontFamily: 'inherit',
  },
  btn: {
    flex: '1 1 auto', background: '#C8F135', color: '#06080C', border: 'none', borderRadius: 10, padding: '14px 22px',
    fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Lexend', sans-serif", cursor: 'pointer',
  },
  error: { minHeight: '1.2em', margin: '12px 0 0', color: '#C8F135', fontSize: '0.82rem' },
  done: { margin: 0, color: '#C8F135', fontSize: '1rem', lineHeight: 1.6 },
  footer: { position: 'absolute', left: 0, right: 0, bottom: 20, textAlign: 'center', padding: '0 16px' },
  legalLink: { color: 'rgba(238,242,255,0.38)', fontSize: '0.78rem', textDecoration: 'none' },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
};
